import React, { useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  Chip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../../utils/config';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

// Cache for tables data with 10-minute TTL
interface TablesCache {
  data: QuickBooksTable[];
  timestamp: number;
}

let tablesCache: TablesCache | null = null;
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

interface TableColumn {
  name: string;
  type: string;
  maxLength: number | null;
  isNullable: string;
  defaultValue: string | null;
}

interface TableDataResponse {
  success: boolean;
  tableName: string;
  columns: TableColumn[];
  data: any[];
  lineItems: any[] | null;
  hasLineItems: boolean;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  error?: string;
  message?: string;
}

interface QuickBooksTable {
  tableName: string;
  displayName: string;
  recordCount: number;
}

interface SyncStatus {
  syncId: string;
  status: 'in_progress' | 'completed' | 'failed' | 'partial';
  startedAt: string;
  completedAt?: string;
  progress: {
    totalEntities: number;
    completedEntities: number;
    currentEntity?: string;
    totalRecords?: number;
    syncedRecords?: number;
    failedRecords?: number;
  };
  errors?: Array<{
    entity: string;
    message: string;
    count: number;
  }>;
}

export function QuickBooksTableDetailPage() {
  const navigate = useNavigate();
  const { tableName } = useParams<{ tableName: string }>();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [availableTables, setAvailableTables] = useState<QuickBooksTable[]>([]);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Fetch available tables on component mount (with caching)
  useEffect(() => {
    const fetchAvailableTables = async () => {
      try {
        // Check if we have cached data that's still valid
        const now = Date.now();
        if (tablesCache && (now - tablesCache.timestamp) < CACHE_TTL) {
          console.log('Using cached tables data');
          setAvailableTables(tablesCache.data);
          return;
        }

        console.log('Fetching fresh tables data');
        const response = await authenticatedFetch(
          `${API_URL}/api/quickbooks/tables`
        );
        if (response && response.ok) {
          const result = await response.json();
          if (result.success) {
            const tables = result.tables || [];
            // Update cache
            tablesCache = {
              data: tables,
              timestamp: now
            };
            setAvailableTables(tables);
          }
        }
      } catch (error) {
        console.error('Error fetching available tables:', error);
        // If there's an error but we have cached data (even if expired), use it
        if (tablesCache) {
          console.log('Using expired cache due to error');
          setAvailableTables(tablesCache.data);
        }
      }
    };
    fetchAvailableTables();
  }, [authenticatedFetch]);

  useEffect(() => {
    let cancelled = false;

    const fetchTableData = async () => {
      if (!tableName || cancelled) return;
      
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          limit: rowsPerPage.toString(),
          offset: (page * rowsPerPage).toString(),
          sortOrder: 'DESC',
        });

        // Sort by txn_date for tables that have it
        const tablesWithTxnDate = ['quickbooks_bills', 'quickbooks_invoices', 'quickbooks_deposits', 'quickbooks_bill_payments'];
        if (tableName && tablesWithTxnDate.includes(tableName)) {
          params.append('sortBy', 'txn_date');
        }

        // Sort webhook events by event_time
        if (tableName === 'quickbooks_webhook_events') {
          params.append('sortBy', 'event_time');
        }

        // Sort sync history by started_at
        if (tableName === 'quickbooks_sync_history') {
          params.append('sortBy', 'started_at');
        }

        if (searchTerm) {
          params.append('search', searchTerm);
        }

        const response = await authenticatedFetch(
          `${API_URL}/api/quickbooks/tables/${tableName}?${params}`
        );
        
        if (cancelled) return;
        
        if (response) {
          const result: TableDataResponse = await response.json();
          
          if (!cancelled) {
            if (result.success) {
              setData(result.data);
              setColumns(result.columns);
              setTotalRows(result.pagination.total);
            } else {
              setError(result.error || 'Failed to load table data');
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching table data:', err);
          setError(err instanceof Error ? err.message : 'Failed to load table data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (tableName) {
      fetchTableData();
    }

    return () => {
      cancelled = true;
    };
  }, [tableName, page, rowsPerPage, searchTerm, authenticatedFetch]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const toggleRowExpanded = (rowId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const formatCellValue = (value: any, column: TableColumn): string => {
    if (value === null || value === undefined) {
      return '-';
    }

    // Format txn_date as date only (no time)
    if (column.name === 'txn_date') {
      return new Date(value).toLocaleDateString();
    }

    // Format event_time for webhook events
    if (column.name === 'event_time') {
      return new Date(value).toLocaleString();
    }

    // Format sync history timestamps
    if (tableName === 'quickbooks_sync_history' && (column.name === 'started_at' || column.name === 'completed_at')) {
      return new Date(value).toLocaleString();
    }

    if (column.type === 'datetime2' || column.type === 'datetime') {
      return new Date(value).toLocaleString();
    }

    if (column.type === 'bit') {
      return value ? 'Yes' : 'No';
    }

    // Format numeric columns in sync history
    if (tableName === 'quickbooks_sync_history' &&
        (column.name === 'total_records' || column.name === 'synced_records' || column.name === 'failed_records')) {
      return parseInt(value).toLocaleString();
    }

    // Format status column in sync history
    if (tableName === 'quickbooks_sync_history' && column.name === 'status') {
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }

    if (column.type === 'decimal' || column.type === 'money') {
      const formattedNumber = parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Add currency symbol for specific columns in bills, invoices, payments, customers, deposits, vendors, and bill payments tables
      if ((tableName === 'quickbooks_bills' && (column.name === 'total_amt' || column.name === 'balance')) ||
          (tableName === 'quickbooks_invoices' && (column.name === 'total_amt' || column.name === 'balance')) ||
          (tableName === 'quickbooks_payments' && (column.name === 'total_amt' || column.name === 'unapplied_amt')) ||
          (tableName === 'quickbooks_customers' && column.name === 'balance') ||
          (tableName === 'quickbooks_deposits' && column.name === 'total_amt') ||
          (tableName === 'quickbooks_vendors' && column.name === 'balance') ||
          (tableName === 'quickbooks_bill_payments' && column.name === 'total_amt')) {
        return `$${formattedNumber}`;
      }

      return formattedNumber;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return value.toString();
  };

  const getDisplayName = (tableName: string): string => {
    return tableName
      .replace('quickbooks_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const getColumnDisplayName = (columnName: string, tableName?: string | null): string => {
    // Special display names for bills table
    if (tableName === 'quickbooks_bills') {
      const billColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'vendor_ref_name': 'Vendor',
        'total_amt': 'Total Amount',
        'balance': 'Balance',
        'currency_ref_value': 'Currency',
        'txn_date': 'Transaction Date'
      };
      if (billColumnNames[columnName]) {
        return billColumnNames[columnName];
      }
    }

    // Special display names for invoices table
    if (tableName === 'quickbooks_invoices') {
      const invoiceColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'customer_ref_name': 'Customer',
        'bill_email': 'Billing Email',
        'total_amt': 'Total Amount',
        'balance': 'Balance Due',
        'currency_ref_value': 'Currency',
        'txn_date': 'Transaction Date'
      };
      if (invoiceColumnNames[columnName]) {
        return invoiceColumnNames[columnName];
      }
    }

    // Special display names for payments table
    if (tableName === 'quickbooks_payments') {
      const paymentColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'customer_ref_name': 'Customer',
        'total_amt': 'Total Amount',
        'unapplied_amt': 'Unapplied Amount',
        'currency_ref_value': 'Currency',
        'created_at': 'Created'
      };
      if (paymentColumnNames[columnName]) {
        return paymentColumnNames[columnName];
      }
    }

    // Special display names for customers table
    if (tableName === 'quickbooks_customers') {
      const customerColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'display_name': 'Display Name',
        'company_name': 'Company',
        'given_name': 'First Name',
        'family_name': 'Last Name',
        'primary_email_addr': 'Email',
        'primary_phone': 'Phone',
        'balance': 'Balance',
        'currency_ref_value': 'Currency',
        'is_active': 'Active',
        'created_at': 'Created'
      };
      if (customerColumnNames[columnName]) {
        return customerColumnNames[columnName];
      }
    }

    // Special display names for deposits table
    if (tableName === 'quickbooks_deposits') {
      const depositColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'deposit_to_account_ref_name': 'Deposit To Account',
        'total_amt': 'Total Amount',
        'currency_ref_value': 'Currency',
        'private_note': 'Private Note',
        'txn_date': 'Transaction Date'
      };
      if (depositColumnNames[columnName]) {
        return depositColumnNames[columnName];
      }
    }

    // Special display names for webhook events table
    if (tableName === 'quickbooks_webhook_events') {
      const webhookColumnNames: { [key: string]: string } = {
        'event_time': 'Event Time',
        'event_type': 'Event Type',
        'entity_name': 'Entity',
        'entity_id': 'Entity ID',
        'operation': 'Operation',
        'processed': 'Processed',
        'payload': 'Payload'
      };
      if (webhookColumnNames[columnName]) {
        return webhookColumnNames[columnName];
      }
    }

    // Special display names for vendors table
    if (tableName === 'quickbooks_vendors') {
      const vendorColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'display_name': 'Display Name',
        'given_name': 'First Name',
        'family_name': 'Last Name',
        'primary_email_addr': 'Email',
        'primary_phone': 'Phone',
        'balance': 'Balance',
        'currency_ref_value': 'Currency',
        'is_active': 'Active'
      };
      if (vendorColumnNames[columnName]) {
        return vendorColumnNames[columnName];
      }
    }

    // Special display names for bill payments table
    if (tableName === 'quickbooks_bill_payments') {
      const billPaymentColumnNames: { [key: string]: string } = {
        'quickbooks_id': 'QuickBooks ID',
        'txn_date': 'Transaction Date',
        'vendor_ref_name': 'Vendor',
        'total_amt': 'Total Amount',
        'currency_ref_value': 'Currency',
        'check_payment_bank_account_ref_name': 'Bank Account'
      };
      if (billPaymentColumnNames[columnName]) {
        return billPaymentColumnNames[columnName];
      }
    }

    // Special display names for sync history table
    if (tableName === 'quickbooks_sync_history') {
      const syncHistoryColumnNames: { [key: string]: string } = {
        'started_at': 'Started At',
        'completed_at': 'Completed At',
        'sync_type': 'Sync Type',
        'total_records': 'Total Records',
        'synced_records': 'Synced Records',
        'failed_records': 'Failed Records',
        'error_message': 'Error Message',
        'status': 'Status'
      };
      if (syncHistoryColumnNames[columnName]) {
        return syncHistoryColumnNames[columnName];
      }
    }

    // Default formatting for other columns
    return columnName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderCellContent = (row: any, column: TableColumn) => {
    const value = row[column.name];

    // Special handling for payload column in webhook events
    if (tableName === 'quickbooks_webhook_events' && column.name === 'payload') {
      const isExpanded = expandedRows.has(row.id);
      let displayValue = value || '';

      try {
        // Try to parse and format JSON
        if (value) {
          const jsonObj = typeof value === 'string' ? JSON.parse(value) : value;
          displayValue = JSON.stringify(jsonObj, null, 2);
        }
      } catch {
        // If not valid JSON, use as is
        displayValue = value || '';
      }

      const truncated = displayValue.length > 80 ? displayValue.substring(0, 80) + '...' : displayValue;

      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
              wordBreak: isExpanded ? 'break-word' : 'normal',
              margin: 0,
              maxWidth: isExpanded ? '600px' : '300px',
              overflow: isExpanded ? 'visible' : 'hidden'
            }}
          >
            {isExpanded ? displayValue : truncated}
          </Typography>
          {displayValue.length > 80 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpanded(row.id);
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      );
    }

    const formattedValue = formatCellValue(value, column);

    // For long text values, truncate and add expand functionality
    if (column.type === 'nvarchar' && column.maxLength === -1 && formattedValue.length > 100) {
      const isExpanded = expandedRows.has(row.id);
      return (
        <Box>
          <Typography variant="body2" component="span">
            {isExpanded ? formattedValue : formattedValue.substring(0, 100) + '...'}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpanded(row.id);
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      );
    }

    return (
      <Typography variant="body2" component="span">
        {formattedValue}
      </Typography>
    );
  };

  // Determine which columns to show (limit to most important ones)
  const getVisibleColumns = () => {
    // Custom column display for specific tables
    if (tableName === 'quickbooks_bills') {
      const billColumns = ['quickbooks_id', 'vendor_ref_name', 'total_amt', 'balance', 'currency_ref_value', 'txn_date'];
      return columns.filter(col => billColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_invoices') {
      const invoiceColumns = ['quickbooks_id', 'customer_ref_name', 'bill_email', 'total_amt', 'balance', 'currency_ref_value', 'txn_date'];
      return columns.filter(col => invoiceColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_payments') {
      const paymentColumns = ['quickbooks_id', 'customer_ref_name', 'total_amt', 'unapplied_amt', 'currency_ref_value', 'created_at'];
      return columns.filter(col => paymentColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_customers') {
      const customerColumns = ['quickbooks_id', 'display_name', 'primary_email_addr', 'balance', 'currency_ref_value', 'is_active'];
      return columns.filter(col => customerColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_deposits') {
      const depositColumns = ['quickbooks_id', 'deposit_to_account_ref_name', 'total_amt', 'currency_ref_value', 'private_note', 'txn_date'];
      return columns.filter(col => depositColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_webhook_events') {
      const webhookColumns = ['event_time', 'event_type', 'entity_name', 'entity_id', 'operation', 'processed', 'payload'];
      return columns.filter(col => webhookColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_vendors') {
      const vendorColumns = ['quickbooks_id', 'display_name', 'given_name', 'family_name', 'primary_email_addr', 'primary_phone', 'balance', 'currency_ref_value', 'is_active'];
      return columns.filter(col => vendorColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_bill_payments') {
      const billPaymentColumns = ['quickbooks_id', 'txn_date', 'vendor_ref_name', 'total_amt', 'currency_ref_value', 'check_payment_bank_account_ref_name'];
      return columns.filter(col => billPaymentColumns.includes(col.name));
    }

    if (tableName === 'quickbooks_sync_history') {
      const syncHistoryColumns = ['started_at', 'completed_at', 'sync_type', 'total_records', 'synced_records', 'failed_records', 'error_message', 'status'];
      return columns.filter(col => syncHistoryColumns.includes(col.name));
    }

    const priorityColumns = ['quickbooks_id', 'name', 'display_name', 'email', 'amount', 'status', 'created_at'];
    const visibleCols = columns.filter(col =>
      priorityColumns.includes(col.name) ||
      columns.length <= 8
    );

    // If we still have too many columns, limit to first 8
    return visibleCols.slice(0, 8);
  };

  // Handle sync start
  const handleStartSync = async () => {
    setSyncing(true);
    setSyncStatus(null);
    setSyncError(null);

    try {
      const response = await authenticatedFetch(`${API_URL}/api/quickbooks/sync/incremental`, {
        method: 'POST',
        body: JSON.stringify({}),
        timeout: 30000 // 30 seconds timeout
      });

      if (response) {
        const data = await response.json();

        if (data.success && data.operationId) {
          // Start polling for operation status
          pollOperationStatus(data.operationId);
        } else {
          throw new Error(data.error || 'Failed to start sync');
        }
      } else {
        throw new Error('No response received from sync endpoint');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncError(error instanceof Error ? error.message : 'Failed to start sync');
      setSyncing(false);
    }
  };

  // Poll for operation status
  const pollOperationStatus = async (operationId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await authenticatedFetch(`${API_URL}/api/quickbooks/operations/${operationId}`, {
          timeout: 15000 // 15 second timeout
        });

        if (response) {
          const data = await response.json();

          // Parse progress string to extract numbers
          let totalEntities = 1;
          let completedEntities = 0;
          let currentEntity = data.progress;

          if (data.progress) {
            const match = data.progress.match(/\((\d+)\/(\d+)\)/);
            if (match) {
              completedEntities = parseInt(match[1], 10) - (data.status === 'processing' ? 1 : 0);
              totalEntities = parseInt(match[2], 10);
            }
          }

          // Transform operation status to sync status format
          setSyncStatus({
            syncId: operationId,
            status: data.status === 'processing' ? 'in_progress' : data.status,
            startedAt: data.createdAt || new Date().toISOString(),
            completedAt: data.completedAt,
            progress: {
              currentEntity: currentEntity,
              totalEntities: totalEntities,
              completedEntities: data.status === 'completed' ? totalEntities : completedEntities
            }
          });

          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(interval);
            setSyncing(false);

            // Show result or error
            if (data.status === 'failed' && data.error) {
              setSyncError(data.error);
            }

            // Refresh table data after successful sync
            if (data.status === 'completed') {
              // Clear cache and reload table data
              tablesCache = null;
              setPage(0);
              window.location.reload(); // Simple reload to refresh all data
            }
          }
        }
      } catch (error) {
        console.error('Error polling operation status:', error);
        clearInterval(interval);
        setSyncing(false);
        setSyncError('Lost connection to sync process');
      }
    }, 2000); // Poll every 2 seconds

    // Clear interval after 10 minutes to prevent indefinite polling
    setTimeout(() => {
      clearInterval(interval);
      setSyncing(false);
      if (syncStatus?.status === 'in_progress') {
        setSyncError('Sync timeout - the process may still be running in the background');
      }
    }, 600000);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Home
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/hub')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Admin Hub
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/quickbooks/tables')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          QuickBooks Tables
        </Link>
        <Typography color="text.primary">
          {tableName ? getDisplayName(tableName) : 'Table Detail'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => navigate('/admin/quickbooks/tables')}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {tableName ? getDisplayName(tableName) : 'Table Detail'}
              </Typography>
              {availableTables.length > 0 && (
                <FormControl size="small" sx={{ ml: 2, minWidth: 200 }}>
                  <Select
                    value={tableName || ''}
                    onChange={(e) => navigate(`/admin/quickbooks/tables/${e.target.value}`)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select a table</MenuItem>
                    {availableTables
                      .sort((a, b) => a.displayName.localeCompare(b.displayName))
                      .map((table) => (
                        <MenuItem key={table.tableName} value={table.tableName}>
                          {table.displayName} ({table.recordCount})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            
            {!loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {totalRows.toLocaleString()} total records
                </Typography>
                {tableName?.startsWith('quickbooks_') && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    onClick={() => setSyncModalOpen(true)}
                    disabled={syncing}
                  >
                    Sync All Tables
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Search Box */}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ width: 300 }}
          />
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      {!loading && !error && data.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {getVisibleColumns().map((column) => (
                    <TableCell key={column.name}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {getColumnDisplayName(column.name, tableName)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {column.type}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={row.id || index} hover>
                    {getVisibleColumns().map((column) => (
                      <TableCell key={column.name}>
                        {renderCellContent(row, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Empty State */}
      {!loading && !error && data.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Records Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm 
              ? `No records match your search "${searchTerm}"`
              : 'This table contains no records.'}
          </Typography>
        </Paper>
      )}

      {/* Sync Modal */}
      <Dialog
        open={syncModalOpen}
        onClose={() => !syncing && setSyncModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SyncIcon />
            QuickBooks Data Sync
          </Box>
        </DialogTitle>
        <DialogContent>
          {!syncing && !syncStatus && (
            <Box>
              <Typography variant="body1" gutterBottom>
                This will perform an incremental sync of all QuickBooks tables.
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Incremental sync will only fetch records that have been modified since the last sync.
                This process may take several minutes depending on the amount of data.
              </Alert>
            </Box>
          )}

          {syncing && syncStatus && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Sync Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(syncStatus.progress.completedEntities / syncStatus.progress.totalEntities) * 100}
                sx={{ mb: 2, height: 8, borderRadius: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {syncStatus.progress.currentEntity || 'Initializing sync...'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Processing entity {syncStatus.progress.completedEntities + 1} of {syncStatus.progress.totalEntities}
              </Typography>
            </Box>
          )}

          {!syncing && syncStatus?.status === 'completed' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Sync completed successfully! The page will refresh to show updated data.
            </Alert>
          )}

          {syncError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {syncError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!syncing && !syncStatus && (
            <>
              <Button onClick={() => setSyncModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleStartSync}
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
              >
                Start Sync
              </Button>
            </>
          )}
          {!syncing && (syncStatus?.status === 'completed' || syncStatus?.status === 'failed' || syncError) && (
            <Button onClick={() => {
              setSyncModalOpen(false);
              setSyncStatus(null);
              setSyncError(null);
            }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}